import { useState } from 'react';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { formatGel } from '../lib/format';
import { parseMoneyToTetri, tetriToInput } from '../lib/money';
import { useSetStaffPay } from '../lib/queries';
import type { OrderItemRow } from '../lib/types';
import { Alert, Button, MoneyInput } from './ui';

/**
 * What the technician gets for one line, and changing it.
 *
 * The figure agreed stays as typed; after a refund the line also says what it
 * now comes to, since that — not the agreed figure — is what goes into the
 * reports.
 */
export function StaffPayEditor({ orderId, item }: { orderId: string; item: OrderItemRow }) {
  const { t } = useI18n();
  const errorText = useApiError();
  const save = useSetStaffPay(orderId);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');

  const open = () => {
    setText(item.staffPayTetri === null ? '' : tetriToInput(item.staffPayTetri));
    setEditing(true);
  };

  if (!editing) {
    const reduced = item.staffPayTetri !== null
      && item.staffPayOwedTetri !== null
      && item.staffPayOwedTetri !== item.staffPayTetri;
    return (
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.7rem]">
        <span className="text-ink-faint">{t('pay.label')}</span>
        <span className="tabular text-ink-dim">
          {item.staffPayTetri === null ? t('pay.notSet') : formatGel(item.staffPayTetri)}
        </span>
        {reduced && (
          <span className="tabular text-warning">
            {t('pay.afterRefund', { amount: formatGel(item.staffPayOwedTetri!) })}
          </span>
        )}
        <button onClick={open} className="text-brand transition hover:underline">
          {item.staffPayTetri === null ? t('pay.set') : t('common.edit')}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      {save.error instanceof ApiError && <Alert>{errorText(save.error) ?? save.error.message}</Alert>}
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <MoneyInput
            label={t('pay.label')}
            value={text}
            autoFocus
            placeholder={t('pay.notSet')}
            onChange={(event) => setText(event.target.value)}
          />
        </div>
        <Button
          loading={save.isPending}
          onClick={() => save.mutate({
            itemId: item.id,
            staffPayTetri: text.trim() === '' ? null : parseMoneyToTetri(text),
          }, { onSuccess: () => setEditing(false) })}
        >
          {t('common.save')}
        </Button>
        <Button variant="ghost" onClick={() => setEditing(false)}>{t('common.cancel')}</Button>
      </div>
    </div>
  );
}
